import React, { useMemo, useState } from 'react';
import { LeftNav } from '../features/left-nav/LeftNav';
import { ChatColumn } from '../features/chat/ChatColumn';
import { EventsRail } from '../features/events-rail/EventsRail';
import { PlanDrawer } from '../features/plan/PlanDrawer';
import { useChats } from '../shared/api/data';
import { useTheme } from './providers/ThemeRoot';
import { USE_MSW } from '../shared/config';
import './App.css';

const THEME_LABEL = { dark: 'Dark', light: 'Light', system: 'System' } as const;

/**
 * The persistent cockpit shell: top bar + left nav | chat | events rail. Surface-switching state
 * (selected project ↔ selected chat) lives here; the plan drawer + secondary surfaces (W2+) mount
 * as overlays over this grid. Data flows through shared/api/data hooks (live routes or fixtures).
 */
export function App(): React.ReactElement {
  const { theme, cycleTheme } = useTheme();
  const chats = useChats();

  // Default to the deep default scenario: the TAS intake brief chat and its project.
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>('p_tas');
  const [selectedChatId, setSelectedChatId] = useState<string | null>('c1');
  const [planOpen, setPlanOpen] = useState(false);

  const selectedChat = useMemo(
    () => chats.data?.data.find((c) => c.id === selectedChatId) ?? null,
    [chats.data, selectedChatId],
  );

  const onSelectChat = (id: string, projectId: string) => {
    setSelectedChatId(id);
    setSelectedProjectId(projectId);
  };

  return (
    <div className="shell" data-plan={planOpen ? 'open' : 'closed'}>
      <header className="topbar">
        <span className="brand">
          <span className="brandDot" aria-hidden />
          Xlooop
        </span>
        {USE_MSW && <span className="topbarMeta">fixtures</span>}
        <span className="topbarSpacer" />
        <button
          type="button"
          className="iconBtn"
          onClick={() => setPlanOpen((v) => !v)}
          aria-pressed={planOpen}
          aria-label={planOpen ? 'Close plan' : 'Open plan'}
        >
          ◱ Plan
        </button>
        <button type="button" className="iconBtn" onClick={cycleTheme} aria-label={`Theme: ${THEME_LABEL[theme]}`}>
          {THEME_LABEL[theme]}
        </button>
      </header>

      <LeftNav
        selectedProjectId={selectedProjectId}
        selectedChatId={selectedChatId}
        onSelectProject={setSelectedProjectId}
        onSelectChat={onSelectChat}
      />

      {planOpen && selectedProjectId && (
        <PlanDrawer scopeId={selectedProjectId} onClose={() => setPlanOpen(false)} />
      )}

      <ChatColumn chat={selectedChat} />

      <EventsRail projectId={selectedProjectId} />
    </div>
  );
}
